/**
 * ModBench.tsx — the Sorry Boss Mod Bench (BETA 22.50)
 *
 * Built to Dylan's mockup, "this is exactly how i want it laid out":
 *   header: MOD BENCH graffiti · WEAPONS / OPEN GOOP tabs · scrap counter · X
 *   left:   WEAPON RACK (pick a weapon to modify)
 *   center: big weapon + name · WEAPON STATS-style mod readout · 6 typed MOD
 *           SLOTS (one per mod type, color-coded) · AVAILABLE MODS cards with
 *           filter tabs
 *   right:  RUN LOADOUT (the 4 "going in" weapon slots) + HOW IT WORKS
 *   bottom: control hints
 *
 * All REAL systems: WEAPON_MODS/weaponModLoadout (equip/unequip, one mod per
 * type — matches the mockup's typed slots), starforgeInventory (owned mods +
 * scrap + GOOP cases), weaponArmed (run loadout), modComboImage (the modded
 * weapon render). Purple identity per the mockup; interactions are CLICK-based
 * (click a mod card to attach, click a filled slot to remove) — drag + gamepad
 * come later.
 */
import { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { resolveAsset } from '../game/uiAssets';
import { WEAPONS } from '../game/loadoutSelection';
import { isWeaponUnlocked } from '../game/loadoutSelection';
import { WEAPON_MODS, MOD_ORDER, MOD_RARITY_COLOR, MOD_RARITY_NAME, modLabel, modComboImage, type ModCode } from '../game/weaponMods';
import { getEquipped, equip, unequip, clearEquipped } from '../game/weaponModLoadout';
import { getInv, openCase, SALVAGE_VALUE } from '../game/starforgeInventory';
import { getArmedWeapons, setArmed, disarm } from '../game/weaponArmed';

const DW = 1600, DH = 900;
const MONO = "'Share Tech Mono', monospace";
const HEAVY = "'Anton', 'Archivo Black', 'Arial Black', sans-serif";
const PURPLE = '#c084fc';
const PURPLE_DIM = 'rgba(168,85,247,0.45)';
const MAX_ARMED = 4;

const NOISE =
  'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'120\' height=\'120\'%3E%3Cfilter id=\'n\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'2\'/%3E%3CfeColorMatrix type=\'saturate\' values=\'0\'/%3E%3C/filter%3E%3Crect width=\'120\' height=\'120\' filter=\'url(%23n)\' opacity=\'0.55\'/%3E%3C/svg%3E")';

// per-mod-type accent (the mockup color-codes its slot headers)
const TYPE_COLOR: Record<ModCode, string> = {
  D: '#ff8a3c', S: '#38bdf8', C: '#a855f7', P: '#2dd4bf', K: '#f43f5e', M: '#ffce4d',
};
const TYPE_LABEL: Record<ModCode, string> = {
  D: 'DAMAGE', S: 'FIRE RATE', C: 'CRIT', P: 'PIERCE', K: 'KNOCKBACK', M: 'MULTISHOT',
};

const panel = (accent = 'rgba(168,85,247,0.22)') => ({
  background:
    'radial-gradient(ellipse 70% 45% at 85% -10%, rgba(150,70,220,0.10), transparent 60%),' +
    'linear-gradient(180deg, rgba(22,20,30,0.95) 0%, rgba(12,11,18,0.96) 100%)',
  border: '2px solid #08060d',
  outline: `1px solid ${accent}`,
  outlineOffset: -4,
  borderRadius: 10,
  boxShadow: 'inset 0 2px 0 rgba(190,150,255,0.08), inset 0 -3px 6px rgba(0,0,0,0.5), 0 5px 14px rgba(0,0,0,0.55)',
} as const);

const Grime = ({ o = 0.09 }: { o?: number }) => (
  <span style={{ position: 'absolute', inset: 0, borderRadius: 8, backgroundImage: NOISE, opacity: o, pointerEvents: 'none', mixBlendMode: 'overlay' }} />
);

export default function ModBench({ onClose }: { onClose: () => void }) {
  const [tab, setTab] = useState<'weapons' | 'goop'>('weapons');
  const [selId, setSelId] = useState<string>(() => getArmedWeapons()[0] ?? WEAPONS.find(w => isWeaponUnlocked(w.id))?.id ?? WEAPONS[0]?.id ?? '');
  const [filter, setFilter] = useState<ModCode | 'ALL'>('ALL');
  const [v, setV] = useState(0);                    // inventory/equip version bump
  const [toast, setToast] = useState<string | null>(null);
  const [scale, setScale] = useState(1);
  const [goopReveal, setGoopReveal] = useState<string | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const bump = () => setV(x => x + 1);

  useEffect(() => {
    const fit = () => {
      const el = wrapRef.current; if (!el) return;
      const s = Math.min(el.clientWidth / DW, el.clientHeight / DH);
      setScale(s > 0 ? s : 1);
    };
    fit();
    const ro = new ResizeObserver(fit);
    if (wrapRef.current) ro.observe(wrapRef.current);
    window.addEventListener('resize', fit);
    return () => { ro.disconnect(); window.removeEventListener('resize', fit); };
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const say = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(t => (t === msg ? null : t)), 1800);
  };

  const inv = useMemo(() => getInv(), [v]);
  const sel = useMemo(() => WEAPONS.find(w => w.id === selId), [selId]);
  const equipped = useMemo(() => (selId ? getEquipped(selId) : []), [selId, v]);
  const codes = equipped.map(e => e.code);
  const armed = useMemo(() => getArmedWeapons(), [v]);
  const selSprite = useMemo(() => {
    if (!sel) return '';
    return (codes.length ? modComboImage(sel.id, codes) : null) ?? resolveAsset(sel.iconUrl);
  }, [sel, codes.join('')]);

  const attach = useCallback((itemId: string, code: ModCode, rarity: 'common' | 'rare' | 'legendary') => {
    if (!sel) return;
    if (!isWeaponUnlocked(sel.id)) { say('WEAPON LOCKED'); return; }
    equip(sel.id, { code, itemId, rarity });
    say(`${WEAPON_MODS[code].name.toUpperCase()} ATTACHED`);
    bump();
  }, [sel]);

  // BETA 22.95 — rarity-organized (Dylan's standing menu rule): grouped
  // common → rare → legendary so tiers read as bands, not acquisition order.
  const _rarityOrder: Record<string, number> = { common: 0, rare: 1, legendary: 2 };
  const ownedFiltered = inv.mods
    .filter(m => filter === 'ALL' || m.code === filter)
    .sort((a, b) => (_rarityOrder[a.rarity] ?? 0) - (_rarityOrder[b.rarity] ?? 0));

  return (
    <div ref={wrapRef} style={{ position: 'fixed', inset: 0, zIndex: 40, background: '#0a070f', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', left: '50%', top: '50%', width: DW, height: DH, transform: `translate(-50%, -50%) scale(${scale})` }}>
        {/* backdrop: near-black with purple energy + ember floor + grunge */}
        <div style={{ position: 'absolute', inset: 0, background:
          'radial-gradient(ellipse 70% 50% at 50% 115%, rgba(255,110,20,0.10) 0%, transparent 55%),' +
          'radial-gradient(ellipse 55% 45% at 12% -8%, rgba(150,70,220,0.16), transparent 60%),' +
          'radial-gradient(ellipse 55% 45% at 90% -8%, rgba(150,70,220,0.12), transparent 60%),' +
          'linear-gradient(180deg, #14101c 0%, #0e0b15 45%, #08060d 100%)' }} />
        <div style={{ position: 'absolute', inset: 0, backgroundImage: NOISE, opacity: 0.08, mixBlendMode: 'overlay', pointerEvents: 'none' }} />

        {/* ── header ── */}
        <div style={{ position: 'absolute', left: 44, top: 18, fontFamily: HEAVY, fontSize: 56, color: PURPLE, transform: 'rotate(-1.2deg)',
          textShadow: '0 3px 0 rgba(60,10,90,0.9), 0 0 26px rgba(168,85,247,0.45), 2px 6px 12px rgba(0,0,0,0.8)', letterSpacing: '0.02em' }}>
          MOD BENCH
        </div>
        {(['weapons', 'goop'] as const).map((t, i) => (
          <button key={t} onClick={() => { setTab(t); setGoopReveal(null); }}
            style={{ position: 'absolute', left: 420 + i * 210, top: 30, height: 54, padding: '0 26px', cursor: 'pointer',
              fontFamily: HEAVY, fontSize: 22, letterSpacing: '0.06em', borderRadius: 8,
              color: tab === t ? '#f4eefc' : '#8d86a0',
              background: tab === t ? 'linear-gradient(180deg, #3b2d52 0%, #241a36 100%)' : 'linear-gradient(180deg, #201c2b 0%, #131019 100%)',
              border: `2px solid ${tab === t ? PURPLE_DIM : '#08060d'}`,
              boxShadow: tab === t ? '0 0 16px rgba(168,85,247,0.35)' : '0 3px 8px rgba(0,0,0,0.5)' }}>
            {t === 'weapons' ? '🔫 WEAPONS' : '💀 OPEN GOOP'}
          </button>
        ))}
        <div style={{ position: 'absolute', right: 120, top: 30, height: 54, padding: '0 22px', display: 'flex', alignItems: 'center', gap: 10,
          ...panel(), borderRadius: 8, fontFamily: HEAVY, fontSize: 22, color: '#e6ddf5' }}>
          <span style={{ fontSize: 20 }}>🟪</span>{inv.scrap.toLocaleString()} SCRAP
        </div>
        <button onClick={onClose} aria-label="close"
          style={{ position: 'absolute', right: 44, top: 30, width: 54, height: 54, cursor: 'pointer', borderRadius: 8,
            fontFamily: HEAVY, fontSize: 26, color: PURPLE, background: 'linear-gradient(180deg, #201c2b 0%, #131019 100%)',
            border: `2px solid ${PURPLE_DIM}`, boxShadow: '0 0 12px rgba(168,85,247,0.3)' }}>✕</button>

        {tab === 'weapons' && (<>
          {/* ── left: WEAPON RACK ── */}
          <div style={{ position: 'absolute', left: 44, top: 108, width: 330, height: 730, ...panel(), padding: '16px 14px' }}>
            <Grime />
            <div style={{ fontFamily: HEAVY, fontSize: 24, color: '#efe9f8', letterSpacing: '0.04em' }}>WEAPON RACK</div>
            <div style={{ fontFamily: MONO, fontSize: 12, color: '#8d86a0', marginBottom: 12 }}>Choose a weapon to modify</div>
            <div style={{ height: 640, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8, paddingRight: 4 }}>
              {WEAPONS.map(w => {
                const unlocked = isWeaponUnlocked(w.id);
                const n = getEquipped(w.id).length;
                const isSel = w.id === selId;
                return (
                  <button key={w.id} onClick={() => setSelId(w.id)}
                    style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', cursor: 'pointer',
                      borderRadius: 8, textAlign: 'left', minHeight: 64, flexShrink: 0,
                      background: isSel ? 'linear-gradient(180deg, #372a4e 0%, #221933 100%)' : 'linear-gradient(180deg, #1b1725 0%, #100d17 100%)',
                      border: `2px solid ${isSel ? PURPLE : '#08060d'}`,
                      boxShadow: isSel ? '0 0 14px rgba(168,85,247,0.4)' : 'none', opacity: unlocked ? 1 : 0.45 }}>
                    <img src={resolveAsset(w.iconUrl)} alt="" style={{ width: 64, height: 40, objectFit: 'contain', flexShrink: 0,
                      filter: 'drop-shadow(0 2px 3px rgba(0,0,0,0.7))' }} />
                    <span style={{ minWidth: 0 }}>
                      <span style={{ display: 'block', fontFamily: HEAVY, fontSize: 15, letterSpacing: '0.04em',
                        color: isSel ? PURPLE : '#d9d2e8', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{w.name.toUpperCase()}</span>
                      <span style={{ display: 'block', fontFamily: MONO, fontSize: 11, color: '#8d86a0' }}>
                        {unlocked ? `${n}/6 MODS` : '🔒 LOCKED'}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* ── center: selected weapon ── */}
          <div style={{ position: 'absolute', left: 400, top: 108, width: 760 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 14 }}>
              <span style={{ fontFamily: HEAVY, fontSize: 42, color: PURPLE, transform: 'rotate(-0.8deg)', display: 'inline-block',
                textShadow: '0 2px 0 rgba(60,10,90,0.9), 0 0 20px rgba(168,85,247,0.4)' }}>{(sel?.name ?? '—').toUpperCase()}</span>
              <span style={{ fontFamily: MONO, fontSize: 14, color: '#8d86a0' }}>{sel?.characterName ? `· ${sel.characterName}` : ''}</span>
            </div>
            <div style={{ position: 'relative', width: 430, height: 240, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse at center, rgba(168,85,247,0.22), transparent 65%)' }} />
              {selSprite && <img key={selSprite} src={selSprite} alt="" style={{ maxWidth: 400, maxHeight: 220, objectFit: 'contain',
                filter: 'drop-shadow(0 8px 14px rgba(0,0,0,0.7))' }} />}
            </div>
            <div style={{ fontFamily: MONO, fontSize: 13, color: '#b9aed3', letterSpacing: '0.12em', marginTop: 2 }}>
              {codes.length} / 6 MODS EQUIPPED
            </div>

            {/* mod slots — one per type, color-coded like the mockup */}
            <div style={{ position: 'absolute', left: 470, top: 52, display: 'grid', gridTemplateColumns: 'repeat(3, 92px)', gap: 8 }}>
              {MOD_ORDER.map(code => {
                const m = WEAPON_MODS[code];
                const eq = equipped.find(e => e.code === code);
                const col = TYPE_COLOR[code];
                return (
                  <button key={code} onClick={() => { if (eq && sel) { unequip(sel.id, eq.itemId); say(`${m.name.toUpperCase()} REMOVED`); bump(); } }}
                    title={eq ? 'Click to remove' : `Empty ${TYPE_LABEL[code]} slot`}
                    style={{ position: 'relative', width: 92, height: 128, cursor: eq ? 'pointer' : 'default', borderRadius: 8, padding: 0,
                      background: 'linear-gradient(180deg, #17131f 0%, #0d0a13 100%)',
                      border: `2px solid ${eq ? col : '#211b2e'}`,
                      boxShadow: eq ? `0 0 12px ${col}44, inset 0 1px 0 rgba(255,255,255,0.06)` : 'inset 0 1px 0 rgba(255,255,255,0.04)' }}>
                    <span style={{ display: 'block', fontFamily: HEAVY, fontSize: 11, letterSpacing: '0.08em', color: col, paddingTop: 6 }}>{TYPE_LABEL[code]}</span>
                    {eq ? (<>
                      <img src={resolveAsset(m.iconUrl)} alt="" style={{ width: 54, height: 54, objectFit: 'contain', margin: '4px auto 0', display: 'block' }} />
                      <span style={{ display: 'block', fontFamily: MONO, fontSize: 9.5, color: MOD_RARITY_COLOR[eq.rarity], lineHeight: 1.25, padding: '3px 4px 0' }}>
                        {modLabel(m, eq.rarity)}
                      </span>
                    </>) : (
                      <span style={{ display: 'block', fontSize: 30, color: '#3d3452', marginTop: 26 }}>+</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* ── available mods ── */}
          <div style={{ position: 'absolute', left: 400, top: 560, width: 760, height: 278, ...panel(), padding: '14px 16px' }}>
            <Grime />
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
              <span style={{ fontFamily: HEAVY, fontSize: 22, color: '#efe9f8', letterSpacing: '0.04em', marginRight: 6 }}>AVAILABLE MODS</span>
              {(['ALL', ...MOD_ORDER] as const).map(f => (
                <button key={f} onClick={() => setFilter(f as ModCode | 'ALL')}
                  style={{ height: 30, padding: '0 12px', cursor: 'pointer', borderRadius: 6, fontFamily: MONO, fontSize: 11, letterSpacing: '0.06em',
                    color: filter === f ? '#0d0a13' : (f === 'ALL' ? '#d9d2e8' : TYPE_COLOR[f as ModCode]),
                    background: filter === f ? (f === 'ALL' ? PURPLE : TYPE_COLOR[f as ModCode]) : 'rgba(20,16,28,0.9)',
                    border: `1px solid ${f === 'ALL' ? PURPLE_DIM : TYPE_COLOR[f as ModCode] + '55'}` }}>
                  {f === 'ALL' ? 'ALL' : TYPE_LABEL[f as ModCode]}
                </button>
              ))}
            </div>
            {ownedFiltered.length === 0 ? (
              <div style={{ fontFamily: MONO, fontSize: 13, color: '#8d86a0', padding: '40px 0', textAlign: 'center' }}>
                No mods owned{filter !== 'ALL' ? ' of this type' : ''} — crack GOOP cases to find mods.
              </div>
            ) : (
              <div style={{ display: 'flex', gap: 10, overflowX: 'auto', paddingBottom: 6 }}>
                {ownedFiltered.map(mi => {
                  const m = WEAPON_MODS[mi.code];
                  const col = MOD_RARITY_COLOR[mi.rarity];
                  const inUse = equipped.some(e => e.itemId === mi.id);
                  return (
                    <button key={mi.id} onClick={() => (inUse ? say('ALREADY ATTACHED') : attach(mi.id, mi.code, mi.rarity))}
                      style={{ position: 'relative', width: 128, flexShrink: 0, cursor: 'pointer', borderRadius: 8, padding: '10px 8px',
                        background: 'linear-gradient(180deg, #1b1725 0%, #100d17 100%)',
                        border: `2px solid ${inUse ? PURPLE : col}55`,
                        boxShadow: inUse ? `0 0 10px ${PURPLE}44` : 'none', opacity: inUse ? 0.55 : 1, textAlign: 'center' }}>
                      <img src={resolveAsset(m.iconUrl)} alt="" style={{ width: 62, height: 62, objectFit: 'contain' }} />
                      <span style={{ display: 'block', fontFamily: HEAVY, fontSize: 13, color: '#e6ddf5', marginTop: 4 }}>{m.name.toUpperCase()}</span>
                      <span style={{ display: 'block', fontFamily: MONO, fontSize: 10, color: TYPE_COLOR[mi.code] }}>{TYPE_LABEL[mi.code]}</span>
                      <span style={{ display: 'block', fontFamily: MONO, fontSize: 10.5, color: col, marginTop: 2 }}>
                        {MOD_RARITY_NAME[mi.rarity]} · {modLabel(m, mi.rarity)}
                      </span>
                      <span style={{ display: 'block', fontFamily: MONO, fontSize: 9.5, color: '#8d86a0', marginTop: 2 }}>
                        {inUse ? 'ATTACHED' : `salvage ${SALVAGE_VALUE[mi.rarity]} scrap`}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* ── right: RUN LOADOUT ── */}
          <div style={{ position: 'absolute', left: 1196, top: 108, width: 360, height: 730, ...panel('rgba(120,220,120,0.18)'), padding: '16px 14px' }}>
            <Grime />
            <div style={{ fontFamily: HEAVY, fontSize: 24, color: '#efe9f8', letterSpacing: '0.04em' }}>RUN LOADOUT</div>
            <div style={{ fontFamily: MONO, fontSize: 12, color: '#8d86a0', marginBottom: 12 }}>These weapons can drop in your run</div>
            {Array.from({ length: MAX_ARMED }, (_, i) => {
              const wid = armed[i];
              const w = wid ? WEAPONS.find(x => x.id === wid) : null;
              return (
                <div key={i} style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 10, minHeight: 68, marginBottom: 10,
                  padding: '8px 10px', borderRadius: 8,
                  background: w ? 'linear-gradient(180deg, #1b2519 0%, #101710 100%)' : 'rgba(14,12,20,0.6)',
                  border: w ? '2px solid rgba(110,220,110,0.5)' : '2px dashed #2a2438' }}>
                  <span style={{ fontFamily: HEAVY, fontSize: 16, color: '#8d86a0', width: 18 }}>{i + 1}</span>
                  {w ? (<>
                    <img src={resolveAsset(w.iconUrl)} alt="" style={{ width: 62, height: 38, objectFit: 'contain', filter: 'drop-shadow(0 2px 3px rgba(0,0,0,0.7))' }} />
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ display: 'block', fontFamily: HEAVY, fontSize: 14, color: '#d9f0d2', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{w.name.toUpperCase()}</span>
                      <span style={{ display: 'block', fontFamily: MONO, fontSize: 10.5, color: '#8d86a0' }}>{getEquipped(w.id).length}/6 MODS</span>
                    </span>
                    <button onClick={() => { disarm(w.id); say(`${w.name.toUpperCase()} REMOVED FROM LOADOUT`); bump(); }}
                      style={{ width: 28, height: 28, cursor: 'pointer', borderRadius: 6, color: '#f0716a', background: 'rgba(40,14,14,0.8)',
                        border: '1px solid rgba(240,113,106,0.4)', fontFamily: MONO, fontSize: 13 }}>✕</button>
                  </>) : (
                    <span style={{ fontFamily: MONO, fontSize: 12, color: '#5d5573' }}>+ EMPTY SLOT — add a weapon</span>
                  )}
                </div>
              );
            })}
            <button onClick={() => {
              if (!sel) return;
              if (!isWeaponUnlocked(sel.id)) { say('WEAPON LOCKED'); return; }
              if (armed.includes(sel.id)) { say('ALREADY IN LOADOUT'); return; }
              if (armed.length >= MAX_ARMED) { say('LOADOUT FULL (MAX 4)'); return; }
              setArmed(sel.id, true); say(`${sel.name.toUpperCase()} ADDED`); bump();
            }}
              style={{ position: 'relative', width: '100%', height: 58, cursor: 'pointer', borderRadius: 8, marginTop: 4,
                fontFamily: HEAVY, fontSize: 20, letterSpacing: '0.05em', color: '#d9f0d2',
                background: 'linear-gradient(180deg, #274a26 0%, #17301a 100%)', border: '2px solid rgba(110,220,110,0.5)',
                boxShadow: '0 0 14px rgba(110,220,110,0.2)' }}>
              + ADD WEAPON <span style={{ fontFamily: MONO, fontSize: 10, display: 'block', letterSpacing: '0.04em', color: '#9dbd97' }}>adds the selected weapon</span>
            </button>
            <div style={{ marginTop: 16, padding: '12px 12px', borderRadius: 8, background: 'rgba(14,12,20,0.7)', border: '1px solid #2a2438' }}>
              <div style={{ fontFamily: HEAVY, fontSize: 16, color: '#c9bfe0', marginBottom: 6 }}>HOW IT WORKS</div>
              {['Select a weapon from the rack.', 'Attach mods to customize it.', 'Add it to your run loadout.', 'Find it in game and wreak havoc.'].map((l, i) => (
                <div key={i} style={{ fontFamily: MONO, fontSize: 11.5, color: '#8d86a0', marginBottom: 3 }}>{i + 1}. {l}</div>
              ))}
              <div style={{ fontFamily: MONO, fontSize: 10.5, color: '#7a7290', marginTop: 6 }}>
                A gun here enters your run as its <span style={{ color: PURPLE }}>modded</span> version — its mods are at risk.
              </div>
            </div>
          </div>

          {/* ── bottom hints ── */}
          <div style={{ position: 'absolute', left: 400, top: 852, display: 'flex', gap: 26, fontFamily: MONO, fontSize: 12, color: '#8d86a0' }}>
            <span>🖱 CLICK MOD = ATTACH</span>
            <span>CLICK SLOT = REMOVE</span>
            <button onClick={() => { if (sel) { clearEquipped(sel.id); say('ALL MODS REMOVED'); bump(); } }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: MONO, fontSize: 12, color: '#f0716a', padding: 0 }}>
              ✕ CLEAR ALL MODS
            </button>
          </div>
        </>)}

        {tab === 'goop' && (
          <div style={{ position: 'absolute', left: 44, top: 108, width: 1512, height: 730, ...panel(), padding: '20px 24px' }}>
            <Grime />
            <div style={{ fontFamily: HEAVY, fontSize: 26, color: '#efe9f8' }}>GOOP CASES</div>
            <div style={{ fontFamily: MONO, fontSize: 12, color: '#8d86a0', marginBottom: 16 }}>
              Crack cases for weapon mods. Earn cases in runs.
            </div>
            {inv.cases.length === 0 ? (
              <div style={{ fontFamily: MONO, fontSize: 14, color: '#8d86a0', padding: '60px 0', textAlign: 'center' }}>
                No cases yet — extract with loot to earn them.
              </div>
            ) : (
              <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
                {inv.cases.map((r, i) => (
                  <button key={i} onClick={() => {
                    const res = openCase(i);
                    setGoopReveal(res.item
                      ? `${MOD_RARITY_NAME[res.item.rarity].toUpperCase()} ${WEAPON_MODS[res.item.code].name.toUpperCase()} — ${modLabel(WEAPON_MODS[res.item.code], res.item.rarity)}`
                      : res.full ? `STASH FULL — +${res.scrapGained} SCRAP` : `+${res.scrapGained} SCRAP`);
                    bump();
                  }}
                    style={{ width: 150, height: 130, cursor: 'pointer', borderRadius: 10, textAlign: 'center',
                      background: 'linear-gradient(180deg, #1b1725 0%, #100d17 100%)', border: `2px solid ${MOD_RARITY_COLOR[r]}`,
                      boxShadow: `0 0 14px ${MOD_RARITY_COLOR[r]}44` }}>
                    <div style={{ fontSize: 40, marginTop: 14 }}>📦</div>
                    <div style={{ fontFamily: HEAVY, fontSize: 14, color: MOD_RARITY_COLOR[r] }}>{MOD_RARITY_NAME[r].toUpperCase()} CASE</div>
                    <div style={{ fontFamily: MONO, fontSize: 10, color: '#8d86a0' }}>click to open</div>
                  </button>
                ))}
              </div>
            )}
            {goopReveal && (
              <div style={{ marginTop: 24, padding: '16px 20px', borderRadius: 10, display: 'inline-block',
                background: 'rgba(30,22,44,0.9)', border: `2px solid ${PURPLE_DIM}`, fontFamily: HEAVY, fontSize: 20, color: PURPLE,
                boxShadow: '0 0 18px rgba(168,85,247,0.35)' }}>
                {goopReveal}
              </div>
            )}
          </div>
        )}

        {toast && (
          <div style={{ position: 'absolute', left: '50%', top: 520, transform: 'translateX(-50%)', zIndex: 10,
            fontFamily: HEAVY, fontSize: 18, letterSpacing: '0.06em', color: '#efe6ff',
            background: 'rgba(20,12,30,0.94)', border: `2px solid ${PURPLE_DIM}`, borderRadius: 8, padding: '10px 22px',
            boxShadow: '0 0 18px rgba(168,85,247,0.4)' }}>{toast}</div>
        )}
      </div>
    </div>
  );
}
